import { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';

interface DragDropZoneProps {
  children: React.ReactNode;
  onFilesDropped: (files: File[]) => void;
}

export function DragDropZone({ children, onFilesDropped }: DragDropZoneProps) {
  const { t } = useTranslation('files');
  const [isDragging, setIsDragging] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setIsDragging(false);
      if (acceptedFiles.length > 0) {
        onFilesDropped(acceptedFiles);
      }
    },
    [onFilesDropped]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
  });

  return (
    <div {...getRootProps()} className="relative min-h-full">
      <input {...getInputProps()} />

      {children}

      {/* Full-screen overlay when dragging */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 bg-primary-500/10 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-12 text-center border-2 border-dashed border-primary-400 dark:border-primary-500"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
              >
                <CloudArrowUpIcon className="w-16 h-16 mx-auto text-primary-500 mb-4" />
              </motion.div>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">{t('dragDrop.dropToUpload')}</p>
              <p className="text-gray-500 dark:text-slate-400 mt-2">{t('dragDrop.releaseToUpload')}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
